<template>
  <div class="max-w-4xl mx-auto">
    <Head :title="'Ticket #' + ticket.id + ' - ' + ticket.subject" />

    <!-- Breadcrumb & Header -->
    <div class="mb-6">
      <div class="flex items-center gap-3 text-sm text-gray-500 mb-2">
        <Link href="/tickets" class="hover:text-orange-500 transition-colors flex items-center gap-1.5 font-medium">
          <i class="fa fa-arrow-left text-xs" />
          <span>Volver a Tickets</span>
        </Link>
        <span class="text-gray-300 font-light">/</span>
        <span class="text-gray-900 font-medium">Ticket #{{ ticket.id }}</span>
      </div>

      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <div>
          <div class="flex items-center gap-3 mb-1">
            <span class="font-mono text-sm font-bold bg-gray-100 text-gray-700 px-2.5 py-0.5 rounded-md">#{{ ticket.id }}</span>
            <h1 class="text-xl font-bold text-gray-900">{{ ticket.subject }}</h1>
          </div>
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-2">
            <span><i class="fa fa-calendar-alt mr-1 text-gray-400" />{{ formatDate(ticket.created_at) }}</span>
            <span><i class="fa fa-tag mr-1 text-gray-400" />{{ ticket.category }}</span>
            <span>
              <i class="fa fa-flag mr-1 text-gray-400" />Prioridad: 
              <strong class="text-gray-700">{{ ticket.priority || 'Normal' }}</strong>
            </span>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <span 
            :class="{
              'bg-blue-50 text-blue-700 border-blue-200': ticket.status === 'Abierto',
              'bg-amber-50 text-amber-700 border-amber-200': ticket.status === 'En progreso',
              'bg-orange-50 text-orange-700 border-orange-200': ticket.status === 'Esperando respuesta del cliente',
              'bg-emerald-50 text-emerald-700 border-emerald-200': ticket.status === 'Resuelto',
              'bg-gray-100 text-gray-700 border-gray-200': !['Abierto', 'En progreso', 'Esperando respuesta del cliente', 'Resuelto'].includes(ticket.status)
            }"
            class="px-3 py-1 inline-flex text-xs font-semibold rounded-full border items-center gap-1.5"
          >
            <span
              class="w-1.5 h-1.5 rounded-full" :class="{
                'bg-blue-500': ticket.status === 'Abierto',
                'bg-amber-500': ticket.status === 'En progreso',
                'bg-orange-500': ticket.status === 'Esperando respuesta del cliente',
                'bg-emerald-500': ticket.status === 'Resuelto',
                'bg-gray-400': !['Abierto', 'En progreso', 'Esperando respuesta del cliente', 'Resuelto'].includes(ticket.status)
              }"
            />
            {{ ticket.status }}
          </span>
        </div>
      </div>
    </div>

    <!-- Timeline / Chat de Conversación -->
    <div class="space-y-4 mb-8">
      <div 
        v-for="msg in ticket.messages" 
        :key="msg.id" 
        class="flex flex-col"
        :class="msg.user_id === ticket.user_id ? 'items-end' : 'items-start'"
      >
        <div 
          class="max-w-2xl rounded-2xl p-5 transition-shadow"
          :class="[
            msg.user_id === ticket.user_id 
              ? 'bg-orange-50/70 border border-orange-200/70 text-gray-900 rounded-tr-none' 
              : 'bg-white border border-gray-100 shadow-sm text-gray-900 rounded-tl-none'
          ]"
        >
          <div class="flex items-center justify-between gap-4 mb-2 pb-2 border-b" :class="msg.user_id === ticket.user_id ? 'border-orange-200/50' : 'border-gray-100'">
            <div class="flex items-center gap-2">
              <div 
                class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                :class="msg.user_id === ticket.user_id ? 'bg-orange-200 text-orange-800' : 'bg-blue-100 text-blue-800'"
              >
                <i class="fa" :class="msg.user_id === ticket.user_id ? 'fa-user' : 'fa-headset'" />
              </div>
              <span class="font-semibold text-xs text-gray-900">
                {{ msg.user ? (msg.user.first_name + ' ' + msg.user.last_name) : 'Soporte Técnico' }}
                <span v-if="msg.user_id === ticket.user_id" class="text-orange-600 font-normal ml-1">(Tú)</span>
                <span v-else class="text-blue-600 font-normal ml-1">(Soporte)</span>
              </span>
            </div>

            <span class="text-[11px] text-gray-400">
              {{ formatDateTime(msg.created_at) }}
            </span>
          </div>

          <div class="text-gray-800 text-sm whitespace-pre-wrap leading-relaxed">
            {{ msg.message }}
          </div>
        </div>
      </div>
    </div>

    <!-- Formulario de Respuesta -->
    <div v-if="ticket.status !== 'Resuelto' && ticket.status !== 'Cerrado'" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
      <div class="p-5 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2">
        <i class="fa fa-reply text-orange-500" />
        <h3 class="text-sm font-semibold text-gray-900">Añadir una respuesta</h3>
      </div>

      <form class="p-5" @submit.prevent="submitReply">
        <div>
          <textarea 
            id="message" 
            v-model="form.message" 
            rows="4" 
            class="block w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm text-gray-900 placeholder-gray-400 transition-colors"
            placeholder="Escribe tu mensaje o respuesta aquí..."
            required
          />
          <p v-if="form.errors.message" class="mt-1.5 text-xs text-red-600 font-medium">{{ form.errors.message }}</p>
        </div>
        
        <div class="mt-4 flex items-center justify-between">
          <span class="text-xs text-gray-400">El equipo de soporte recibirá una notificación de tu respuesta.</span>

          <loading-button 
            :loading="form.processing"
            type="submit" 
            class="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg text-sm transition-colors shadow-sm flex items-center gap-2"
          >
            <i class="fa fa-paper-plane text-xs" />
            <span>Enviar Respuesta</span>
          </loading-button>
        </div>
      </form>
    </div>
    
    <!-- Banner de Ticket Resuelto -->
    <div v-else class="bg-emerald-50 rounded-xl p-6 text-center border border-emerald-200/60 mb-8">
      <div class="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
        <i class="fa fa-check text-xl" />
      </div>
      <h3 class="text-base font-bold text-emerald-900">Este ticket ha sido resuelto</h3>
      <p class="mt-1 text-sm text-emerald-700 max-w-md mx-auto">
        La consulta o inconveniente fue atendido por el equipo técnico. Si necesitas asistencia adicional, puedes abrir una nueva solicitud.
      </p>
      <div class="mt-4">
        <Link 
          href="/tickets/create" 
          class="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-lg shadow-sm transition-colors"
        >
          <i class="fa fa-plus" />
          <span>Crear Nuevo Ticket</span>
        </Link>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  name: 'TicketsShow',

  components: {
    Head,
    Link,
    LoadingButton,
  },

  layout: Layout,

  props: {
    ticket: Object,
  },

  data() {
    return {
      form: this.$inertia.form({
        message: '',
      }),
    }
  },

  methods: {
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('es-VE', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric', 
      })
    },

    formatDateTime(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('es-VE', { 
        dateStyle: 'medium',
        timeStyle: 'short',
        hour12: false, 
      })
    },

    submitReply() {
      this.form.post(`/tickets/${this.ticket.id}/reply`, {
        preserveScroll: true,
        only: ['ticket'],
        onSuccess: () => this.form.reset('message'),
      })
    },
  },
}
</script>
